import { useState, useEffect, useRef, useMemo } from 'react'
import { TreeSelect, Tag } from 'antd'
import debounce from 'lodash/debounce'
import '../../../styleLess/FilterSearchTree.scss'

const resolveNodeTitle = (item, custom) => {
  if (custom?.title) return custom.title
  if (item?.title) return item.title
  const name = item?.name_en || item?.name_th || ''
  const label = [item?.code, name].filter((part) => part && part !== '-').join(' ')
  return label || String(item?.id ?? '')
}

const normalizeSelection = (values, optionData) => {
  if (!values?.length) return []
  if (values.includes(0)) {
    return optionData.map((item) => item.value)
  }
  return values.filter((value) => value !== 0)
}

const FilterSearchTree = ({
  dataItems,
  selectedItems,
  setSelectedItems,
  itemTypeName,
  treeId,
  customTreeData,
  excludeAllID,
}) => {
  const [optionData, setOptionData] = useState([])
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [draftSelection, setDraftSelection] = useState(selectedItems || [])
  const pendingSelectionRef = useRef(selectedItems || [])

  const debouncedCommit = useMemo(
    () =>
      debounce((values, commit) => {
        commit(values)
      }, 250),
    [],
  )

  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit])

  useEffect(() => {
    if (dataItems?.length > 0) {
      const configData = dataItems.map((e, i) => {
        if (customTreeData) {
          const custom = customTreeData(e)
          const title = resolveNodeTitle(e, custom)
          return { key: `0-${i + 1}`, ...custom, title, label: title }
        }
        const label = e.name_en ? e.name_en : e.name_th
        return {
          title: label,
          label,
          value: e.id,
          key: `0-${i + 1}`,
        }
      })

      const allOption = {
        title: 'All',
        label: 'All',
        value: 0,
        key: '0-0',
      }

      setOptionData(excludeAllID ? configData : [allOption, ...configData])
    } else {
      setOptionData([])
    }
  }, [dataItems, excludeAllID])

  useEffect(() => {
    if (!dropdownOpen) {
      const next = selectedItems || []
      setDraftSelection(next)
      pendingSelectionRef.current = next
    }
  }, [selectedItems, dropdownOpen])

  const commitSelection = (values) => {
    const normalized = normalizeSelection(values, optionData)
    pendingSelectionRef.current = normalized
    setDraftSelection(normalized)
    debouncedCommit.cancel()
    setSelectedItems(normalized)
  }

  const handleChange = (values) => {
    const normalized = normalizeSelection(values, optionData)
    pendingSelectionRef.current = normalized
    setDraftSelection(normalized)

    if (dropdownOpen) {
      debouncedCommit(normalized, setSelectedItems)
      return
    }

    debouncedCommit.cancel()
    setSelectedItems(normalized)
  }

  const handleDropdownVisibleChange = (open) => {
    setDropdownOpen(open)

    if (open) {
      const current = selectedItems || []
      setDraftSelection(current)
      pendingSelectionRef.current = current
      return
    }

    debouncedCommit.cancel()
    setSelectedItems(pendingSelectionRef.current)
  }

  const displaySelection = dropdownOpen ? draftSelection : selectedItems

  return (
    <TreeSelect
      onClear={() => commitSelection([])}
      allowClear
      showSearch
      treeNodeFilterProp="title"
      filterTreeNode={(search, item) => {
        const title = item?.title ?? item?.label ?? ''
        if (typeof title !== 'string') return false
        return title.toLowerCase().includes(search.toLowerCase())
      }}
      id={treeId}
      style={{ minWidth: '280px', width: '300px', marginRight: 8 }}
      size="large"
      maxTagCount={1}
      listHeight={300}
      dropdownClassName="filter-search-tree-dropdown"
      treeCheckable
      value={displaySelection}
      showCheckedStrategy="SHOW_PARENT"
      onChange={handleChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      placeholder="Select Business"
      maxTagPlaceholder={(event) => {
        const find = displaySelection?.find((fi) => fi === 0)
        return (
          <span
            style={{
              display: find !== undefined ? 'none' : '',
            }}
          >
            {`and ${event?.length} other ${itemTypeName}`}
          </span>
        )
      }}
      tagRender={(event) => {
        const { value, label } = event
        const find = displaySelection?.find((fi) => fi === 0)
        if (value === 0) {
          return (
            <span id={`tag-${label}`} style={{ paddingLeft: 8, fontSize: 16 }}>
              All Businesses
            </span>
          )
        }
        return (
          <Tag
            style={{
              display: find === 0 && value !== 0 ? 'none' : 'flex',
              fontSize: 12,
              borderRadius: '16px',
              background: 'rgba(7, 78, 159, 0.1)',
              height: '32px',
              alignItems: 'center',
              color: '#074E9F',
            }}
          >
            <span
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '150px',
              }}
              id={`tag-${label}`}
            >
              {label}
            </span>
          </Tag>
        )
      }}
      treeData={optionData}
    />
  )
}

export default FilterSearchTree
